import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FilterPills, usePhone } from './filter-pills'

export interface ViewOption<V extends string = string> {
  value: V
  label: string
  /** Shown below 640 px, so more options fit before "More". */
  shortLabel?: string
  icon?: LucideIcon
  count?: number | null
  /** A short green tag after the label, as the checkout's "−17%". */
  badge?: string | null
}

export interface ViewSwitchProps<V extends string = string> {
  value: V
  onChange: (value: V) => void
  /** 2 to 4 options; 5 or more is a `SectionNav`. */
  options: ViewOption<V>[]
  'aria-label': string
  /** Icons only (labels for screen readers), for a compact one-line toolbar on a narrow screen. */
  iconsOnly?: boolean
  className?: string
  'data-testid'?: string
}

/**
 * The view of a list (F-182, layer 1): what the whole page shows, such as
 * All · Mine · Under review. The Feed's pills (`FilterPills`): one line,
 * what does not fit goes into "More ▾", a 40 px touch target on a phone.
 * Semantically a tab list: one tab stop, arrow keys move and select.
 * Never stack it over another row of pills: a second cut is a `FilterMenu`.
 */
export function ViewSwitch<V extends string = string>({ value, onChange, options, iconsOnly = false, className, ...rest }: ViewSwitchProps<V>) {
  const phone = usePhone()
  return (
    <FilterPills
      semantics="tabs"
      max={Number.POSITIVE_INFINITY}
      value={value}
      onChange={onChange}
      options={options.map((o) => ({ value: o.value, label: phone && o.shortLabel ? o.shortLabel : o.label, icon: o.icon, count: o.count, badge: o.badge, hideLabel: iconsOnly && Boolean(o.icon) }))}
      aria-label={rest['aria-label']}
      data-testid={rest['data-testid']}
      className={cn('w-full', className)}
    />
  )
}
